'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import SvgCaretDown from '../components/icons/CaretDown';
import { cn } from '../lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

/**
 * Which disclosure glyph the trigger draws.
 *
 * `chevron` is lucide's stroked `ChevronDown` and is the DEFAULT, because it is
 * what all thirty-odd existing call sites already render — Figma's own
 * `Rows per page` control included (12135-44223: two 3px strokes meeting at a
 * point, 10px across, open at the top).
 *
 * `filled` is the solid triangle from `components/icons/CaretDown` — an exported
 * path of 5.32x3.70 — which Figma draws on `Group by` (12156-35742) and on
 * the rules drawer's preview-scope select (12135-42953). Two designs, a few
 * hundred pixels apart, in the same file: that split is precisely why this is a
 * per-trigger opt-in and not a global swap.
 */
type SelectCaret = 'chevron' | 'filled';

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { showArrow?: boolean; caret?: SelectCaret }
>(({ className, children, showArrow = true, caret = 'chevron', ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-neutral-700 bg-popover px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:font-normal data-[placeholder]:text-neutral-400 [&>span]:line-clamp-1',
      className,
    )}
    {...props}
  >
    {children}
    {/* `showArrow` is the older, coarser switch and it still wins: a trigger
        that wants no glyph must not sprout one by naming a caret shape. */}
    {showArrow &&
      (caret === 'filled' ? (
        // NOT `asChild`: `SvgCaretDown` is a plain function component, so a
        // forwarded ref would draw React's "function components cannot be given
        // refs" warning. The span `Icon` renders by itself costs nothing.
        //
        // SIZE — `size-2.5`, the one value every `SvgCaretDown` on the Content
        // Labels surface carries. `CaretDown` is a 320x512 viewBox in a square
        // em box under `xMidYMid meet`, so 512 units map onto 1em and its ink is
        // only 0.5807em x 0.3294em of that box: at 10px, 5.81 x 3.29px against
        // Figma's exported 5.32 x 3.70 path. Ink-to-ink, never box-to-box.
        // Matched on the WIDTH — the major axis, and the one the eye reads as
        // "how big is the caret". The axes cannot both land (our ink is 1.76:1
        // where Figma's is 1.44:1 and an em scales both together), so the 0.4px
        // shortfall on a sub-4px minor axis is the one to eat; equal-ink-area
        // independently lands on 10.1px. `size-3` was 6.97px wide, 31% over.
        // Full derivation and the rejected "6 x 4" reading: Select.component.test.
        //
        // INK — explicit, NOT inherited. `fill="currentColor"` used to hand this
        // branch whatever ink the trigger's text carried, which is how one Figma
        // token became four (neutral-700 on Group by, neutral-900 on the rules
        // drawer's preview scope). `neutral-400` (#8A8C9E) measures 3.32:1 on
        // white. RATIFIED DEVIATION: the frames tint this #A6A7B5, which measures
        // 2.38:1 and misses the 3:1 SC 1.4.11 asks of a control's affordance;
        // neutral-400 is the closest value on the ramp that passes.
        <SelectPrimitive.Icon className="flex shrink-0 items-center justify-center">
          <SvgCaretDown className="size-2.5 text-neutral-400" />
        </SelectPrimitive.Icon>
      ) : (
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </SelectPrimitive.Icon>
      ))}
  </SelectPrimitive.Trigger>
));

SelectTrigger.displayName = 'SelectTrigger';

export default SelectTrigger;
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-[3500] max-h-96 min-w-[8rem] overflow-hidden rounded-lg border border-neutral-700 bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label ref={ref} className={cn('py-1.5 pl-8 pr-2 text-sm font-normal', className)} {...props} />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-pointer select-none items-center rounded-md py-1.5 pl-8 pr-2 text-sm font-light outline-none transition-all focus:bg-accent-dark focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>

    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-muted', className)} {...props} />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
